import { z } from "zod";
import { ResumableUploadError } from "./ResumableUploadError";
import { crc32cFile } from "./crc32cFile";
import { uploadBlobResumably } from "./resumableTransport";
import type { UploadPolicy } from "../../constants/uploadPolicies";
import {
  defaultApiRetryDelay,
  FinalizedUploadSchema,
  finalizeWithReconciliation,
  retryTransientApiRequest,
  type ApiRetryDelay,
  type FinalizedUpload,
  UploadStatusSchema,
  waitForRecoveredFinalization,
} from "./resumableApiRecovery";

export { ResumableUploadError } from "./ResumableUploadError";
export type { FinalizedUpload } from "./resumableApiRecovery";

const UploadPolicySchema = z.object({
  key: z.string().min(1),
  max_file_bytes: z.number().int().positive(),
  max_total_bytes: z.number().int().positive(),
  min_files: z.number().int().positive().default(1),
  max_files: z.number().int().positive(),
  processing_chunk_bytes: z.number().int().positive(),
  processing_chunk_pages: z.number().int().positive(),
  chunk_processing: z.boolean(),
  max_image_bytes: z.number().int().positive().default(20_000_000),
});

const InitiatedUploadSchema = z.object({
  upload_id: z.string().min(1),
  batch_id: z.string().min(1),
  upload_url: z.string().url(),
  chunk_size_bytes: z.number().int().positive(),
  expires_at: z.string().min(1),
  policy: UploadPolicySchema,
});

const ApiFailureSchema = z.object({
  response: z.object({
    status: z.number().int().optional(),
    data: z.object({
      code: z.string().optional(),
      backendCode: z.string().optional(),
      error: z.string().optional(),
    }),
  }),
});

const CancelledBatchSchema = z.object({
  status: z.enum(["cancelled", "already_submitted"]),
  cancelled: z.number().int().nonnegative().default(0),
  rolled_back: z.number().int().nonnegative().default(0),
  already_submitted: z.boolean().default(false),
});

const ConfirmedBatchSchema = z.object({
  status: z.literal("submitted"),
  submitted: z.number().int().positive(),
});

export type InitiateUploadInput = {
  readonly filename: string;
  readonly content_type: string;
  readonly size_bytes: number;
  readonly batch_id: string;
  readonly batch_file_count: number;
  readonly batch_index: number;
  readonly checksum: string;
  readonly idempotency_key: string;
  readonly document_type?: string;
  readonly upload_batch_action?: "replace_evidence";
};

export interface ResumableUploadApi {
  initiateUpload(
    requirementId: string,
    input: InitiateUploadInput,
  ): Promise<unknown>;
  finalizeUpload(requirementId: string, uploadId: string): Promise<unknown>;
  getUploadStatus?(requirementId: string, uploadId: string): Promise<unknown>;
  cancelUploadBatch(requirementId: string, batchId: string): Promise<unknown>;
  confirmUploadBatch?(requirementId: string, batchId: string): Promise<unknown>;
}

type UploadFilesParams = {
  readonly api: ResumableUploadApi;
  readonly requirementId: string;
  readonly files: readonly File[];
  readonly uploadPolicy: UploadPolicy;
  readonly batchId?: string;
  readonly documentType?: string;
  readonly uploadBatchAction?: "replace_evidence";
  readonly createRequest?: () => XMLHttpRequest;
  readonly calculateChecksum?: (file: File) => Promise<string>;
  readonly createIdempotencyKey?: () => string;
  readonly apiRetryDelay?: ApiRetryDelay;
  readonly recoveryStorage?: Pick<
    Storage,
    "getItem" | "setItem" | "removeItem"
  > | null;
  readonly recoveryScope?: string;
  readonly onProgress?: (percent: number) => void;
  readonly onBatchReady?: (batchId: string) => void;
};

const RECOVERY_STORAGE_PREFIX = "multiburo:resumable-upload";

type RecoveryFileIdentity = {
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly checksum: string;
  readonly idempotencyKey: string;
  uploadId?: string;
};

type RecoveryBatchIdentity = {
  readonly batchId: string;
  readonly documentType?: string;
  readonly files: RecoveryFileIdentity[];
  readonly committedAt?: number;
};

const SUPPORTED_CONTENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function inferredContentType(file: File): string {
  const reportedType = file.type.toLowerCase();
  if (file.name.toLowerCase().endsWith(".pdf")) {
    return "application/pdf";
  }
  return reportedType || "application/octet-stream";
}

function validateBatchAgainstPolicy(
  files: readonly { readonly file: File; readonly contentType: string }[],
  policy: UploadPolicy,
): void {
  if (files.length < policy.min_files) {
    throw new ResumableUploadError(
      "UPLOAD_TOO_FEW_FILES",
      `Debes subir al menos ${policy.min_files} archivo${policy.min_files === 1 ? "" : "s"}.`,
    );
  }

  if (files.length > policy.max_files) {
    throw new ResumableUploadError(
      "UPLOAD_TOO_MANY_FILES",
      "El lote contiene demasiados archivos para este documento.",
    );
  }

  let totalBytes = 0;
  for (const { file, contentType } of files) {
    if (!SUPPORTED_CONTENT_TYPES.has(contentType)) {
      throw new ResumableUploadError(
        "UPLOAD_UNSUPPORTED_MEDIA_TYPE",
        `El formato de ${file.name} no es compatible.`,
      );
    }
    const isImage = contentType.startsWith("image/");
    const maxBytes = isImage ? policy.max_image_bytes : policy.max_file_bytes;
    if (file.size > maxBytes) {
      throw new ResumableUploadError(
        "UPLOAD_FILE_TOO_LARGE",
        `El archivo ${file.name} excede el límite permitido.`,
      );
    }
    totalBytes += file.size;
  }

  if (totalBytes > policy.max_total_bytes) {
    throw new ResumableUploadError(
      "UPLOAD_TOTAL_TOO_LARGE",
      "Los archivos exceden el límite total permitido.",
    );
  }
}

function defaultRecoveryStorage(): Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
> | null {
  try {
    return typeof globalThis.localStorage === "undefined"
      ? null
      : globalThis.localStorage;
  } catch {
    return null;
  }
}

function recoveryStorageKey(requirementId: string, recoveryScope = ""): string {
  const scope = recoveryScope ? `:${recoveryScope}` : "";
  return `${RECOVERY_STORAGE_PREFIX}${scope}:${requirementId}`;
}

function readRecoveryBatch(
  storage: Pick<Storage, "getItem"> | null,
  requirementId: string,
  recoveryScope = "",
): RecoveryBatchIdentity | null {
  if (!storage) return null;
  try {
    const serialized = storage.getItem(
      recoveryStorageKey(requirementId, recoveryScope),
    );
    if (!serialized) return null;
    const value = JSON.parse(serialized) as RecoveryBatchIdentity;
    if (!value.batchId || !Array.isArray(value.files)) return null;
    return value;
  } catch {
    return null;
  }
}

function writeRecoveryBatch(
  storage: Pick<Storage, "setItem"> | null,
  requirementId: string,
  batch: RecoveryBatchIdentity,
  recoveryScope = "",
): void {
  if (!storage) return;
  try {
    storage.setItem(
      recoveryStorageKey(requirementId, recoveryScope),
      JSON.stringify(batch),
    );
  } catch {
    // Uploading remains available when browser storage is unavailable or full.
  }
}

function clearRecoveryBatch(
  storage: Pick<Storage, "getItem" | "removeItem"> | null,
  requirementId: string,
  batchId: string,
  recoveryScope = "",
): void {
  if (!storage) return;
  try {
    const current = readRecoveryBatch(storage, requirementId, recoveryScope);
    if (current?.batchId === batchId) {
      storage.removeItem(recoveryStorageKey(requirementId, recoveryScope));
    }
  } catch {
    // A completed upload must not fail because browser storage is unavailable.
  }
}

export function persistUploadBatchForSubmission(
  requirementId: string,
  batchId: string,
  documentType?: string,
  recoveryStorage: Pick<Storage, "setItem"> | null = defaultRecoveryStorage(),
  recoveryScope = "",
): void {
  writeRecoveryBatch(
    recoveryStorage,
    requirementId,
    { batchId, documentType, files: [] },
    recoveryScope,
  );
}

export async function cancelPendingUploadBatch(
  api: {
    confirmUploadBatch(
      requirementId: string,
      batchId: string,
    ): Promise<unknown>;
    cancelUploadBatch(requirementId: string, batchId: string): Promise<unknown>;
  },
  requirementId: string,
  recoveryStorage: Pick<
    Storage,
    "getItem" | "removeItem"
  > | null = defaultRecoveryStorage(),
  recoveryScope = "",
): Promise<void> {
  const pending = readRecoveryBatch(
    recoveryStorage,
    requirementId,
    recoveryScope,
  );
  if (!pending) return;

  if (pending.committedAt) {
    try {
      await confirmResumableUploadSubmission(
        api,
        requirementId,
        pending.batchId,
        recoveryStorage,
        recoveryScope,
      );
      return;
    } catch (error: unknown) {
      if (isUploadRecoveryInaccessible(error)) {
        clearRecoveryBatch(
          recoveryStorage,
          requirementId,
          pending.batchId,
          recoveryScope,
        );
        return;
      }
      // A committed batch must never be cancelled. Wait until the server can
      // reconcile it before allowing a replacement transfer.
      throw apiError(error);
    }
  }

  try {
    const result = CancelledBatchSchema.parse(
      await api.cancelUploadBatch(requirementId, pending.batchId),
    );
    if (result.already_submitted || result.status === "already_submitted") {
      clearRecoveryBatch(
        recoveryStorage,
        requirementId,
        pending.batchId,
        recoveryScope,
      );
      throw new ResumableUploadError(
        "UPLOAD_ALREADY_SUBMITTED",
        "La carga anterior ya fue enviada. Actualiza la página antes de volver a subir.",
      );
    }
    if (result.status !== "cancelled") {
      throw new ResumableUploadError(
        "UPLOAD_CANCEL_UNCONFIRMED",
        "No pudimos liberar la carga anterior.",
      );
    }
    clearRecoveryBatch(
      recoveryStorage,
      requirementId,
      pending.batchId,
      recoveryScope,
    );
  } catch (error: unknown) {
    if (isUploadPrincipalMismatch(error)) {
      clearRecoveryBatch(
        recoveryStorage,
        requirementId,
        pending.batchId,
        recoveryScope,
      );
      return;
    }
    throw apiError(error);
  }
}

export async function confirmResumableUploadSubmission(
  api: {
    confirmUploadBatch(
      requirementId: string,
      batchId: string,
    ): Promise<unknown>;
  },
  requirementId: string,
  batchId: string,
  recoveryStorage: Pick<
    Storage,
    "getItem" | "removeItem"
  > | null = defaultRecoveryStorage(),
  recoveryScope = "",
): Promise<void> {
  const confirmed = ConfirmedBatchSchema.parse(
    await api.confirmUploadBatch(requirementId, batchId),
  );
  if (confirmed.status !== "submitted") return;
  clearRecoveryBatch(recoveryStorage, requirementId, batchId, recoveryScope);
}

export async function confirmUploadSubmissionBestEffort(
  api: {
    confirmUploadBatch(
      requirementId: string,
      batchId: string,
    ): Promise<unknown>;
  },
  requirementId: string,
  batchId: string,
  recoveryStorage: Pick<
    Storage,
    "getItem" | "setItem" | "removeItem"
  > | null = defaultRecoveryStorage(),
  recoveryScope = "",
): Promise<boolean> {
  const pending = readRecoveryBatch(
    recoveryStorage,
    requirementId,
    recoveryScope,
  );
  if (pending?.batchId === batchId) {
    writeRecoveryBatch(
      recoveryStorage,
      requirementId,
      { ...pending, committedAt: Date.now() },
      recoveryScope,
    );
  }
  try {
    await confirmResumableUploadSubmission(
      api,
      requirementId,
      batchId,
      recoveryStorage,
      recoveryScope,
    );
    return true;
  } catch {
    // Metadata/status is already committed. Its marked recovery record lets
    // a later attempt reconcile (but never cancel) the submitted batch.
    return false;
  }
}

function recoveryMatches(
  recovery: RecoveryBatchIdentity | null,
  documentType: string | undefined,
  files: readonly Omit<RecoveryFileIdentity, "idempotencyKey" | "uploadId">[],
): recovery is RecoveryBatchIdentity {
  return Boolean(
    recovery &&
      recovery.documentType === documentType &&
      recovery.files.length === files.length &&
      files.every((file, index) => {
        const recovered = recovery.files[index];
        return (
          recovered?.filename === file.filename &&
          recovered.contentType === file.contentType &&
          recovered.sizeBytes === file.sizeBytes &&
          recovered.checksum === file.checksum
        );
      }),
  );
}

function apiError(error: unknown): ResumableUploadError {
  const parsed = ApiFailureSchema.safeParse(error);
  if (parsed.success) {
    return new ResumableUploadError(
      parsed.data.response.data.code ?? "UPLOAD_FAILED",
      parsed.data.response.data.error ?? "No pudimos subir el archivo",
      parsed.data.response.status,
    );
  }
  if (error instanceof ResumableUploadError) return error;
  return new ResumableUploadError(
    "UPLOAD_FAILED",
    "No pudimos subir el archivo. Intenta nuevamente.",
  );
}

function apiFailureCode(error: unknown): string | undefined {
  const parsed = ApiFailureSchema.safeParse(error);
  return parsed.success ? parsed.data.response.data.code : undefined;
}

function isUploadPrincipalMismatch(error: unknown): boolean {
  const parsed = ApiFailureSchema.safeParse(error);
  return Boolean(
    parsed.success &&
      parsed.data.response.status === 403 &&
      (parsed.data.response.data.backendCode ??
        parsed.data.response.data.code) === "UPLOAD_PRINCIPAL_MISMATCH",
  );
}

function isUploadRecoveryInaccessible(error: unknown): boolean {
  if (isUploadPrincipalMismatch(error)) return true;
  const parsed = ApiFailureSchema.safeParse(error);
  return Boolean(
    parsed.success &&
      parsed.data.response.status === 404 &&
      parsed.data.response.data.code === "UPLOAD_NOT_FOUND",
  );
}

function isTerminalRecoveryStatus(
  status: z.infer<typeof UploadStatusSchema>["status"],
): boolean {
  return ["failed", "cleanup_pending", "expired"].includes(status);
}

export async function uploadFilesResumably({
  api,
  requirementId,
  files,
  uploadPolicy,
  batchId,
  documentType,
  uploadBatchAction,
  createRequest = () => new XMLHttpRequest(),
  calculateChecksum = crc32cFile,
  createIdempotencyKey = () => crypto.randomUUID(),
  apiRetryDelay = defaultApiRetryDelay,
  recoveryStorage = defaultRecoveryStorage(),
  recoveryScope = "",
  onProgress,
  onBatchReady,
}: UploadFilesParams): Promise<readonly FinalizedUpload[]> {
  const totalBytes = files.reduce((total, file) => total + file.size, 0);
  const finalizedUploads = new Map<number, FinalizedUpload>();
  const batchFileCount = files.length;
  let completedBytes = 0;
  let highestProgress = 0;
  onProgress?.(0);

  for (const file of files) {
    if (file.size <= 0) {
      throw new ResumableUploadError(
        "UPLOAD_EMPTY_FILE",
        "El archivo está vacío",
      );
    }
  }

  const contentTypedFiles = files.map((file) => ({
    file,
    contentType: inferredContentType(file),
  }));
  const policy = UploadPolicySchema.parse(uploadPolicy);
  validateBatchAgainstPolicy(contentTypedFiles, policy);

  const checksummedFiles: Array<{
    readonly file: File;
    readonly checksum: string;
    readonly contentType: string;
  }> = [];
  for (const { file, contentType } of contentTypedFiles) {
    checksummedFiles.push({
      file,
      checksum: await calculateChecksum(file),
      contentType,
    });
  }

  let recoveryCandidate = readRecoveryBatch(
    recoveryStorage,
    requirementId,
    recoveryScope,
  );
  if (recoveryCandidate?.committedAt) {
    const committedRecovery = recoveryCandidate;
    if (!api.confirmUploadBatch) {
      throw new ResumableUploadError(
        "UPLOAD_CONFIRMATION_PENDING",
        "La carga anterior sigue pendiente de confirmación.",
      );
    }
    try {
      await confirmResumableUploadSubmission(
        { confirmUploadBatch: api.confirmUploadBatch },
        requirementId,
        committedRecovery.batchId,
        recoveryStorage,
        recoveryScope,
      );
      recoveryCandidate = null;
    } catch (error: unknown) {
      if (!isUploadRecoveryInaccessible(error)) throw apiError(error);
      clearRecoveryBatch(
        recoveryStorage,
        requirementId,
        committedRecovery.batchId,
        recoveryScope,
      );
      recoveryCandidate = null;
    }
  }
  const recoveryFiles = checksummedFiles.map(
    ({ file, checksum, contentType }) => ({
      filename: file.name,
      contentType,
      sizeBytes: file.size,
      checksum,
    }),
  );
  const reusableRecovery =
    batchId === undefined &&
    recoveryMatches(recoveryCandidate, documentType, recoveryFiles)
      ? recoveryCandidate
      : null;
  const cancelRecoveryBatch = async (
    recovery: RecoveryBatchIdentity,
  ): Promise<void> => {
    try {
      const result = CancelledBatchSchema.parse(
        await retryTransientApiRequest(
          () => api.cancelUploadBatch(requirementId, recovery.batchId),
          apiRetryDelay,
        ),
      );
      if (result.already_submitted || result.status === "already_submitted") {
        clearRecoveryBatch(
          recoveryStorage,
          requirementId,
          recovery.batchId,
          recoveryScope,
        );
        throw new ResumableUploadError(
          "UPLOAD_ALREADY_SUBMITTED",
          "La carga anterior ya fue enviada. Actualiza la página antes de volver a subir.",
        );
      }
      if (result.status !== "cancelled") {
        throw new ResumableUploadError(
          "UPLOAD_CANCEL_UNCONFIRMED",
          "No pudimos liberar la carga anterior.",
        );
      }
    } catch (error: unknown) {
      if (isUploadPrincipalMismatch(error)) {
        clearRecoveryBatch(
          recoveryStorage,
          requirementId,
          recovery.batchId,
          recoveryScope,
        );
        return;
      }
      throw apiError(error);
    }
    clearRecoveryBatch(
      recoveryStorage,
      requirementId,
      recovery.batchId,
      recoveryScope,
    );
  };
  if (recoveryCandidate && !reusableRecovery) {
    await cancelRecoveryBatch(recoveryCandidate);
  }
  const createRecoveryBatch = (): RecoveryBatchIdentity => ({
    batchId: batchId ?? crypto.randomUUID(),
    documentType,
    files: recoveryFiles.map((file) => ({
      ...file,
      idempotencyKey: createIdempotencyKey(),
    })),
  });
  let recoveryBatch: RecoveryBatchIdentity =
    reusableRecovery ?? createRecoveryBatch();
  const recoveredStatuses = new Map<
    number,
    z.infer<typeof UploadStatusSchema>
  >();

  if (reusableRecovery && api.getUploadStatus) {
    try {
      for (const [index, recoveredFile] of reusableRecovery.files.entries()) {
        if (!recoveredFile.uploadId) continue;
        const persisted = UploadStatusSchema.parse(
          await retryTransientApiRequest(
            () => api.getUploadStatus!(requirementId, recoveredFile.uploadId!),
            apiRetryDelay,
          ),
        );
        recoveredStatuses.set(index, persisted);
      }
    } catch (error: unknown) {
      const parsed = ApiFailureSchema.safeParse(error);
      const missingSession =
        parsed.success && [404, 410].includes(parsed.data.response.status ?? 0);
      const principalChanged =
        parsed.success && parsed.data.response.status === 403;
      if (principalChanged) {
        clearRecoveryBatch(
          recoveryStorage,
          requirementId,
          reusableRecovery.batchId,
          recoveryScope,
        );
        recoveredStatuses.clear();
        recoveryBatch = createRecoveryBatch();
      }
      if (!principalChanged) {
        if (!missingSession) throw apiError(error);
        recoveredStatuses.clear();
        await cancelRecoveryBatch(reusableRecovery);
        recoveryBatch = createRecoveryBatch();
      }
    }

    if (
      [...recoveredStatuses.values()].some((persisted) =>
        isTerminalRecoveryStatus(persisted.status),
      )
    ) {
      recoveredStatuses.clear();
      await cancelRecoveryBatch(reusableRecovery);
      recoveryBatch = createRecoveryBatch();
    }
  }
  writeRecoveryBatch(
    recoveryStorage,
    requirementId,
    recoveryBatch,
    recoveryScope,
  );
  // Recovery reconciliation can replace an inaccessible, missing, or
  // terminal batch. Publish only the final identity that will be used by
  // initiate/finalize so callers never PATCH or confirm a discarded batch.
  onBatchReady?.(recoveryBatch.batchId);

  const preparedFiles = checksummedFiles.map((prepared, index) => ({
    ...prepared,
    idempotencyKey: recoveryBatch.files[index].idempotencyKey,
    uploadId: recoveryBatch.files[index].uploadId,
    index,
  }));

  const initiatedUploads: Array<{
    readonly file: File;
    readonly index: number;
    readonly initiated: z.infer<typeof InitiatedUploadSchema>;
  }> = [];
  for (const {
    file,
    checksum,
    contentType,
    idempotencyKey,
    uploadId,
    index,
  } of preparedFiles) {
    try {
      if (uploadId && api.getUploadStatus) {
        const persisted =
          recoveredStatuses.get(index) ??
          UploadStatusSchema.parse(
            await retryTransientApiRequest(
              () => api.getUploadStatus!(requirementId, uploadId),
              apiRetryDelay,
            ),
          );
        if (persisted.status === "finalized") {
          const finalized = {
            upload_id: persisted.upload_id,
            document_file_id: persisted.document_file_id,
            status: persisted.status,
          };
          finalizedUploads.set(index, FinalizedUploadSchema.parse(finalized));
          completedBytes += file.size;
          continue;
        }
        if (persisted.status === "finalizing") {
          const finalized = await waitForRecoveredFinalization(
            () => api.getUploadStatus!(requirementId, uploadId),
            apiRetryDelay,
          );
          if (finalized) {
            finalizedUploads.set(index, finalized);
            completedBytes += file.size;
            continue;
          }
        }
        if (persisted.status === "initiated") {
          try {
            const finalized = await finalizeWithReconciliation(
              {
                finalize: () => api.finalizeUpload(requirementId, uploadId),
                getStatus: () => api.getUploadStatus!(requirementId, uploadId),
              },
              apiRetryDelay,
            );
            finalizedUploads.set(index, finalized);
            completedBytes += file.size;
            continue;
          } catch (error: unknown) {
            const failureCode = apiFailureCode(error);
            if (failureCode === "UPLOAD_NOT_FINALIZABLE") {
              const finalized = await waitForRecoveredFinalization(
                () => api.getUploadStatus!(requirementId, uploadId),
                apiRetryDelay,
              );
              if (finalized) {
                finalizedUploads.set(index, finalized);
                completedBytes += file.size;
                continue;
              }
            } else if (failureCode !== "UPLOAD_INCOMPLETE") {
              throw error;
            }
          }
        } else if (isTerminalRecoveryStatus(persisted.status)) {
          throw new ResumableUploadError(
            "UPLOAD_NOT_RECOVERABLE",
            "La carga anterior ya no se puede reanudar.",
          );
        }
      }
      const initiateInput: InitiateUploadInput = {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
        batch_id: recoveryBatch.batchId,
        batch_file_count: batchFileCount,
        batch_index: index,
        checksum,
        idempotency_key: idempotencyKey,
        ...(documentType ? { document_type: documentType } : {}),
        ...(uploadBatchAction
          ? { upload_batch_action: uploadBatchAction }
          : {}),
      };
      const initiatedResponse = await retryTransientApiRequest(
        () => api.initiateUpload(requirementId, initiateInput),
        apiRetryDelay,
      );
      const initiated = InitiatedUploadSchema.parse(initiatedResponse);
      if (initiated.batch_id !== recoveryBatch.batchId) {
        throw new ResumableUploadError(
          "UPLOAD_BATCH_MISMATCH",
          "La sesión iniciada no coincide con el lote solicitado.",
        );
      }
      recoveryBatch.files[index].uploadId = initiated.upload_id;
      writeRecoveryBatch(
        recoveryStorage,
        requirementId,
        recoveryBatch,
        recoveryScope,
      );
      initiatedUploads.push({ file, index, initiated });
    } catch (error: unknown) {
      throw apiError(error);
    }
  }

  if (completedBytes > 0 && totalBytes > 0) {
    highestProgress = Math.max(
      highestProgress,
      Math.round((completedBytes / totalBytes) * 100),
    );
    onProgress?.(highestProgress);
  }

  for (const { file, index, initiated } of initiatedUploads) {
    await uploadBlobResumably({
      uploadUrl: initiated.upload_url,
      file,
      chunkSize: initiated.chunk_size_bytes,
      createRequest,
      onProgress: (uploadedBytes) => {
        const percent = Math.min(
          100,
          Math.round(((completedBytes + uploadedBytes) / totalBytes) * 100),
        );
        highestProgress = Math.max(highestProgress, percent);
        onProgress?.(highestProgress);
      },
    });

    try {
      const finalize = () =>
        api.finalizeUpload(requirementId, initiated.upload_id);
      const getUploadStatus = api.getUploadStatus;
      const finalized = await finalizeWithReconciliation(
        getUploadStatus
          ? {
              finalize,
              getStatus: () =>
                getUploadStatus(requirementId, initiated.upload_id),
            }
          : { finalize },
        apiRetryDelay,
      );
      finalizedUploads.set(index, finalized);
    } catch (error: unknown) {
      throw apiError(error);
    }
    completedBytes += file.size;
    highestProgress = Math.max(
      highestProgress,
      Math.round((completedBytes / totalBytes) * 100),
    );
    onProgress?.(highestProgress);
  }

  const orderedFinalized = files.map((_, index) => finalizedUploads.get(index));
  if (orderedFinalized.some((upload) => upload === undefined)) {
    throw new ResumableUploadError(
      "UPLOAD_RECOVERY_INCOMPLETE",
      "No pudimos confirmar todos los archivos del lote.",
    );
  }
  return orderedFinalized as FinalizedUpload[];
}
